import type {
  GetTrainingResponse,
  TrainingArtifact,
  TrainingArtifactKind,
  TrainingResource,
} from "@scoach/types";

import { api, buildAuthHeaders } from "../../lib/http.ts";

export type TrainingRefreshMode = "meetings" | "role";

/** "My ongoing training" — per-user learning coach + native study materials. */
export const trainingApi = {
  get: () => api<GetTrainingResponse>("/training"),

  /** Regenerate from meetings or from the user's role (fresh latest-product curriculum). */
  refresh: (mode: TrainingRefreshMode) =>
    api<GetTrainingResponse>("/training/refresh", { method: "POST", body: { mode } }),

  /** Append more learning topics to the current plan. */
  more: () => api<GetTrainingResponse>("/training/more", { method: "POST", body: {} }),

  /** Find real external resources (videos/articles/docs) via grounded web search — appends to the plan. */
  resources: () => api<GetTrainingResponse>("/training/resources", { method: "POST", body: {} }),

  /** Topic-scoped resource lookup (for the Add-content modal) — not persisted. */
  findResources: (topic: string) =>
    api<{ resources: TrainingResource[] }>("/training/resources", {
      method: "POST",
      body: { topic },
    }).then((r) => r.resources),

  /** Generate a study material (optionally focused on a topic + custom prompt).
   * `length` applies to podcasts/audio: "short" clip, "standard", or "long" deep-dive. */
  generateArtifact: (
    kind: TrainingArtifactKind,
    topic?: string,
    prompt?: string,
    length?: "short" | "standard" | "long",
    language?: string,
  ) =>
    api<{ artifact: TrainingArtifact }>("/training/artifacts", {
      method: "POST",
      body: {
        kind,
        ...(topic ? { topic } : {}),
        ...(prompt ? { prompt } : {}),
        ...(length && length !== "standard" ? { length } : {}),
        ...(language && language !== "auto" ? { language } : {}),
      },
    }),

  /** Mark a material (own or community) done / not-done. Returns the new completed set. */
  complete: (id: string, done: boolean) =>
    api<{ completed: string[] }>("/training/complete", { method: "POST", body: { id, done } }),

  /** Fetch an artifact's bytes (auth-gated route) as an object URL, for in-page display. */
  loadArtifact: async (url: string): Promise<{ blobUrl: string; contentType: string }> => {
    const headers = await buildAuthHeaders();
    const res = await fetch(url, { headers });
    if (!res.ok) throw new Error("Couldn't load the material");
    const blob = await res.blob();
    return { blobUrl: URL.createObjectURL(blob), contentType: blob.type };
  },
};
